import { IconButton, Link, Skeleton } from '@mui/material';
import { useMemo } from 'react';
import { DynamicIcon, Tooltip } from 'src/components';
import { formatSupportPhoneWithExt } from 'src/components/FormattedPhoneInput/format-support-phone-with-ext';
import { useBrandActivationLinks } from 'src/graphql/entities/activation-flow-config';
import { type BrandWithSupport, useBrandSitesCount } from 'src/graphql/entities/brand';
import {
  SegmentCard,
  SegmentCardHeader,
  SegmentDivider,
  SegmentFieldGrid,
  SegmentFieldLabel,
  SegmentFieldValue,
  SegmentHeaderActions,
  SegmentLink,
  SegmentLinkRow,
  SegmentName,
  SitesCountChip,
} from './styled';

interface ClientSegmentCardProps {
  segment: BrandWithSupport;
  canManage: boolean;
  onEdit: (segment: BrandWithSupport, sitesCount: number) => void;
  onDelete: (segment: BrandWithSupport, sitesCount: number) => void;
}

const EMPTY_VALUE = '—';

const formatText = (value: string | null | undefined): string =>
  value === null || value === undefined || value === '' ? EMPTY_VALUE : value;

const formatPhone = (
  value: number | string | null | undefined,
  ext?: number | string | null,
): string => {
  if (value === null || value === undefined || value === '') return EMPTY_VALUE;
  return formatSupportPhoneWithExt(value, ext) || EMPTY_VALUE;
};

interface ActivationLinkValueProps {
  loading: boolean;
  url: string | null | undefined;
}

const ActivationLinkValue = ({ loading, url }: ActivationLinkValueProps) => {
  if (loading) {
    return <Skeleton variant="rounded" width="15rem" height="1.25rem" />;
  }
  if (!url) {
    return <SegmentFieldValue>{EMPTY_VALUE}</SegmentFieldValue>;
  }
  return (
    <SegmentLinkRow>
      <SegmentLink href={url} target="_blank" rel="noopener noreferrer" title={url}>
        {url}
      </SegmentLink>
      <Link
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Open link in new tab"
        sx={{ display: 'inline-flex', alignItems: 'center', lineHeight: 0 }}
      >
        <DynamicIcon
          className="launch-icon"
          name="arrow-up-right"
          library="lucide"
          size={16}
        />
      </Link>
    </SegmentLinkRow>
  );
};

export const ClientSegmentCard = ({
  segment,
  canManage,
  onEdit,
  onDelete,
}: ClientSegmentCardProps) => {
  const { sitesCount, loading: sitesCountLoading } = useBrandSitesCount(
    segment.partnerId ?? null,
    segment.brandId,
  );

  const {
    termsOfServiceUrl,
    privacyPolicyUrl,
    contactSupportLink,
    loading: activationLinksLoading,
  } = useBrandActivationLinks({
    partnerId: segment.partnerId ?? null,
    brandId: segment.brandId,
  });

  const brandGroup = segment.brandGroup;
  const supportPhone = brandGroup?.fwdPhonePublished ?? brandGroup?.fwdPhoneSupport;
  const supportPhoneExt = brandGroup?.fwdPhonePublished
    ? brandGroup.fwdPhonePublishedExt
    : null;

  const sitesBadgeLabel = useMemo(
    () => `${sitesCount} ${sitesCount === 1 ? 'site' : 'sites'}`,
    [sitesCount],
  );

  return (
    <SegmentCard>
      <SegmentCardHeader>
        <SegmentName title={segment.name ?? undefined}>
          {segment.name ?? 'Unnamed segment'}
        </SegmentName>
        <SegmentHeaderActions>
          {sitesCountLoading ? (
            <Skeleton variant="rounded" width="3.5rem" height="1.375rem" />
          ) : (
            <SitesCountChip label={sitesBadgeLabel} size="small" />
          )}
          {canManage ? (
            <>
              <Tooltip title="Edit segment">
                <IconButton
                  size="small"
                  onClick={() => onEdit(segment, sitesCount)}
                  aria-label="Edit segment"
                >
                  <DynamicIcon name="pencil" library="lucide" size={20} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete segment">
                <IconButton
                  size="small"
                  onClick={() => onDelete(segment, sitesCount)}
                  aria-label="Delete segment"
                >
                  <DynamicIcon name="trash-2" library="lucide" size={20} />
                </IconButton>
              </Tooltip>
            </>
          ) : null}
        </SegmentHeaderActions>
      </SegmentCardHeader>

      <SegmentDivider />

      <SegmentFieldGrid>
        <SegmentFieldLabel>Display name</SegmentFieldLabel>
        <SegmentFieldValue title={segment.displayName ?? undefined}>
          {formatText(segment.displayName)}
        </SegmentFieldValue>

        <SegmentFieldLabel>Support phone</SegmentFieldLabel>
        <SegmentFieldValue>{formatPhone(supportPhone, supportPhoneExt)}</SegmentFieldValue>

        <SegmentFieldLabel>Support email</SegmentFieldLabel>
        <SegmentFieldValue title={brandGroup?.supportEmail ?? undefined}>
          {formatText(brandGroup?.supportEmail)}
        </SegmentFieldValue>

        <SegmentFieldLabel>Membership email</SegmentFieldLabel>
        <SegmentFieldValue title={brandGroup?.membershipEmail ?? undefined}>
          {formatText(brandGroup?.membershipEmail)}
        </SegmentFieldValue>

        <SegmentFieldLabel>Terms of Service</SegmentFieldLabel>
        <ActivationLinkValue loading={activationLinksLoading} url={termsOfServiceUrl} />

        <SegmentFieldLabel>Privacy Policy</SegmentFieldLabel>
        <ActivationLinkValue loading={activationLinksLoading} url={privacyPolicyUrl} />

        <SegmentFieldLabel>Calendly</SegmentFieldLabel>
        <ActivationLinkValue loading={activationLinksLoading} url={contactSupportLink} />
      </SegmentFieldGrid>
    </SegmentCard>
  );
};
